<template>
  <div class="employee-details-ext-view">
    <EmployeeDetailsExt
      :employee-id="employeeId"
      :allow-holiday-requests="allowHolidayRequests"
      :show-documents="false"
      :show-balance-update="false"
      :fill-viewport="false"
      :show-top-balance-card="false"
      :show-flux-summary="false"
      v-bind="$attrs"
    />
    <v-container class="vacation-overview pa-0" fluid>
      <v-row v-if="loading" class="justify-center py-6">
        <v-progress-circular indeterminate color="primary" size="32" />
      </v-row>

      <template v-else>
        <v-row dense>
          <v-col cols="12" md="6" lg="7">
            <v-card outlined class="info-card">
              <v-card-title class="info-title">
                <v-icon size="18" class="mr-2">mdi-briefcase</v-icon>
                {{ t("employeeDetails.balance.title") }}
              </v-card-title>
              <v-divider />
              <v-card-text>
                <div class="balance-grid">
                  <div>
                    <div class="text-caption">{{ t("employeeDetails.balance.annual") }}</div>
                    <div class="text-h6">{{ formatNumber(balanceSummary.annual) }}</div>
                  </div>
                  <div>
                    <div class="text-caption">{{ t("employeeDetails.balance.totalAccrued") }}</div>
                    <div class="text-h6">{{ formatNumber(balanceSummary.totalAccrued) }}</div>
                  </div>
                  <div>
                    <div class="text-caption">{{ t("employeeDetails.balance.taken") }}</div>
                    <div class="text-h6 text-error">{{ formatNumber(balanceSummary.totalTaken) }}</div>
                  </div>
                  <div>
                    <div class="text-caption">{{ t("employeeDetails.balance.remaining") }}</div>
                    <div class="text-h6 text-success">{{ formatNumber(balanceSummary.remaining) }}</div>
                  </div>
                </div>
                <v-alert
                  v-if="!hasBalance"
                  type="warning"
                  density="compact"
                  class="mt-3"
                >
                  {{ t("employeeHolidays.balance.balanceMissing") || t("employeeDetails.balance.missing", "Soldul de concediu nu este configurat pentru acest angajat.") }}
                </v-alert>
              </v-card-text>
            </v-card>

            <v-card outlined class="info-card mt-4">
              <v-card-title class="info-title d-flex align-center">
                {{ t("employeeDetails.history.title") }}
                <v-spacer></v-spacer>
                <v-btn
                  v-if="sortedRequests.length"
                  size="x-small"
                  variant="text"
                  color="primary"
                  @click="toggleShowAllRequests"
                >
                  {{ showAllRequests ? t("employeeDetails.history.hide") : t("employeeDetails.history.showAll") }}
                </v-btn>
              </v-card-title>
              <v-divider />
              <v-card-text class="requests-list">
                <div v-if="!sortedRequests.length" class="text-caption text-medium-emphasis">
                  {{ t("employeeDetails.history.none") }}
                </div>
                <template v-else>
                  <div
                    v-for="request in visibleRequests"
                    :key="request.id"
                    :class="['request-row', isPastRequest(request) ? 'request-row--past' : 'request-row--upcoming']"
                  >
                    <div class="request-row__header">
                      <div class="request-row__title">
                        <v-chip size="x-small" :color="statusChipColor(request.status, request.pending_action)">
                          {{ statusLabel(request.status, request.pending_action) }}
                        </v-chip>
                        <span class="ml-2">
                          {{ request.holiday_type_name || lookupHolidayType(request.holiday_type_id) || t("employeeDetails.history.unknownType") }}
                        </span>
                      </div>
                    <div class="request-row__meta">
                      <span class="text-caption mr-2">{{ formatNumber(getWorkingDays(request)) }} {{ t("employeeDetails.flow.days") }}</span>
                      <div v-if="!companyPdfDisabled" class="request-row__downloads">
                        <v-tooltip :text="$t('employeeHolidays.history.downloadRequest')">
                          <template #activator="{ props }">
                            <v-btn
                              icon
                              size="small"
                              variant="text"
                              :loading="pdfDownloadLoadingId === `request-${request.id}`"
                              v-bind="props"
                              @click="downloadPdf(request, 'request')"
                            >
                              <v-icon size="18">mdi-download</v-icon>
                            </v-btn>
                          </template>
                        </v-tooltip>
                        <v-tooltip v-if="request.status === 'approved'" :text="$t('employeeHolidays.history.downloadOrder')">
                          <template #activator="{ props }">
                            <v-btn
                              icon
                              size="small"
                              variant="text"
                              color="primary"
                              :loading="pdfDownloadLoadingId === `order-${request.id}`"
                              v-bind="props"
                              @click="downloadPdf(request, 'order')"
                            >
                              <v-icon size="18">mdi-file-download-outline</v-icon>
                            </v-btn>
                          </template>
                          </v-tooltip>
                        </div>
                      </div>
                    </div>
                    <div class="text-caption mb-1">
                      {{ formatDate(request.start_date) }} – {{ formatDate(request.end_date) }}
                    </div>
                    <div v-if="request.pending_action === 'cancel'" class="text-caption text-error mb-1">
                      {{ t("employeeDetails.history.statusPendingCancel") }}
                    </div>
                    <div v-if="request.notes" class="text-caption mb-2">{{ request.notes }}</div>
                  </div>
                  <div class="d-flex justify-center mt-1">
                    <v-btn
                      v-if="hasMoreRequests"
                      size="small"
                      variant="text"
                      color="primary"
                      @click="showMore"
                    >
                      {{ t("employeeDetails.history.showMore") }}
                    </v-btn>
                  </div>
                </template>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="6" lg="5">
            <v-card outlined class="info-card calendar-card">
              <v-card-title class="info-title d-flex align-center">
                <v-icon size="18" class="mr-2">mdi-calendar-month</v-icon>
                {{ t("employeeDetails.calendar.title") }}
              </v-card-title>
              <v-divider />
              <v-card-text>
                <div class="calendar-wrapper">
                  <VCalendar
                    class="vacation-calendar"
                    type="month"
                    :model-value="calendarSelection"
                    @update:model-value="onCalendarModelUpdate"
                    :events="calendarEvents"
                    :event-color="eventColorByEvent"
                    color="primary"
                    :weekdays="[1, 2, 3, 4, 5, 6, 0]"
                  >
                    <template #event="{ event }">
                      <div
                        v-if="event?.meta?.highlightKind"
                        class="calendar-day-overlay"
                        :class="`calendar-day-overlay--${event.meta.highlightKind}`"
                      ></div>
                      <v-chip
                        class="calendar-event-chip"
                        :color="event.color"
                        label
                      >
                        <span class="calendar-event-chip__dot" :class="`bg-${event.color}`"></span>
                        {{ event.title }}
                      </v-chip>
                    </template>
                  </VCalendar>
                </div>
                <div class="calendar-legend mt-3">
                  <div class="legend-item">
                    <span class="legend-dot approved" />
                    {{ t("employeeDetails.calendar.legend.approved") }}
                  </div>
                  <div class="legend-item">
                    <span class="legend-dot pending" />
                    {{ t("employeeDetails.calendar.legend.pending") }}
                  </div>
                  <div class="legend-item">
                    <span class="legend-dot non-working" />
                    {{ t("employeeDetails.calendar.legend.nonWorking") }}
                  </div>
                  <div class="legend-item">
                    <span class="legend-dot short-day" />
                    {{ t("employeeDetails.calendar.legend.shortDay") }}
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </div>

  <v-snackbar
    v-model="pdfSnack.open"
    :timeout="3000"
    :color="pdfSnack.color"
    location="bottom right"
  >
    {{ pdfSnack.text }}
  </v-snackbar>
</template>

<script>
import axios from "axios";
import { defineComponent, computed, ref, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import EmployeeDetailsExt from "./EmployeeDetailsExt.vue";
import { VCalendar } from "vuetify/labs/VCalendar";
import {
  toNumber,
  formatNumber,
  formatDate,
  formatAsIsoDate,
  normalizeDateString,
  toDateObject,
  computeAccruedUntilYearEnd,
} from "@/Utile/holidayFormatting.mjs";
import { computeWorkingDays as computeWorkingDaysUtil } from "@/Utile/computeWorkingDays.mjs";
import { decideHighlight, buildShortDaySetFromNonWorking } from "@/Utile/calendarHighlight.mjs";

const DISPLAYED_CALENDAR_STATUSES = new Set(["pending", "approved"]);
const INITIAL_VISIBLE_REQUESTS = 4;
const LOAD_MORE_STEP = 10;

export default defineComponent({
  name: "EmployeeDetailsExtView",
  components: {
    EmployeeDetailsExt,
    VCalendar,
  },
  inheritAttrs: false,
  props: {
    employeeId: {
      type: [Number, String],
      default: null,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const route = useRoute();
    const allowHolidayRequests = computed(() => Boolean(route.query.openHolidayRequest));
    const employeeId = computed(() => props.employeeId || route.params.id || route.query.id || null);

    const loading = ref(true);
    const employee = ref({});
    const holidayBalance = ref(null);
    const holidayRequests = ref([]);
    const holidayTypes = ref([]);
    const nonWorkingDays = ref([]);
    const pdfDownloadLoadingId = ref(null);
    const pdfSnack = ref({
      open: false,
      color: "success",
      text: "",
    });
    const companyPdfDisabled = ref(false);
    const pdfDownloadLoading = ref(false);

    const todayIso = formatAsIsoDate(new Date());
    const calendarSelection = ref([]);
    const initialCalendarDate = toDateObject(todayIso);
    if (initialCalendarDate) {
      calendarSelection.value = [initialCalendarDate];
    }
    const visibleLimit = ref(INITIAL_VISIBLE_REQUESTS);
    const showAllRequests = ref(false);

    const holidayTypeMap = computed(() => {
      const map = new Map();
      holidayTypes.value.forEach((type) => {
        map.set(Number(type.id), type);
      });
      return map;
    });

    const lookupHolidayType = (id) => {
      const type = holidayTypeMap.value.get(Number(id));
      return type ? type.name_ro : null;
    };

    const hasBalance = computed(() => Boolean(holidayBalance.value));
    const balanceSummary = computed(() => {
      const annual = toNumber(holidayBalance.value?.annual_leave_days, 0);
      const carry = toNumber(holidayBalance.value?.carryover_days, 0);
      const accrualStartRaw = holidayBalance.value?.accrual_start_date;
      const totalAccrued = computeAccruedUntilYearEnd(
        annual,
        carry,
        accrualStartRaw,
        props.employee?.holiday_balance?.accrual_finish_date || null
      );
      const totalTaken = totalTakenDisplay.value;
      return {
        annual,
        totalAccrued,
        totalTaken,
        remaining: Number((totalAccrued - totalTaken).toFixed(2)),
      };
    });

    const totalTakenDisplay = computed(() => {
      const fromBalance = toNumber(holidayBalance.value?.total_taken_days, 0);
      if (fromBalance > 0) return fromBalance;
      return holidayRequests.value
        .filter((req) => req.status === "approved")
        .reduce((sum, req) => sum + getWorkingDays(req), 0);
    });

    const nonWorkingSet = computed(() => {
      const set = new Set();
      nonWorkingDays.value.forEach((entry) => {
        const iso = normalizeDateString(entry?.occurrence_date || entry?.date);
        if (iso) {
          set.add(iso);
        }
      });
      return set;
    });

    const computeDaySetForStatus = (status) => {
      const set = new Set();
      holidayRequests.value.forEach((req) => {
        if (req.status !== status) return;
        const start = toDateObject(req.start_date);
        const end = toDateObject(req.end_date);
        if (!start || !end) return;
        const cursor = new Date(start);
        while (cursor <= end) {
          const iso = formatAsIsoDate(cursor);
          if (iso) {
            set.add(iso);
          }
          cursor.setDate(cursor.getDate() + 1);
        }
      });
      return set;
    };

    const calendarEvents = computed(() => {
      const days = new Map();
      const nonWorkingIsoSet = new Set();

      const ensureDay = (iso) => {
        const bucket = days.get(iso) || { events: [], kinds: new Set() };
        if (!days.has(iso)) days.set(iso, bucket);
        return bucket;
      };

      nonWorkingDays.value.forEach((entry) => {
        const iso = normalizeDateString(entry?.occurrence_date || entry?.date);
        const date = toDateObject(iso);
        if (!date) return;
        nonWorkingIsoSet.add(iso);
        const ev = {
          start: date,
          end: date,
          color: "neutral",
          title: entry?.name || t("employeeDetails.calendar.legend.nonWorking"),
          allDay: true,
          meta: { kind: "non-working", iso, highlightKind: null },
        };
        const bucket = ensureDay(iso);
        bucket.events.push(ev);
        bucket.kinds.add("non-working");
      });

      const shortDaySet = buildShortDaySetFromNonWorking(nonWorkingDays.value);
      shortDaySet.forEach((iso) => {
        if (nonWorkingIsoSet.has(iso)) return;
        const date = toDateObject(iso);
        if (!date) return;
        const ev = {
          start: date,
          end: date,
          color: "info",
          title: t("employeeDetails.calendar.legend.shortDay"),
          allDay: true,
          meta: { kind: "short-day", iso, highlightKind: null },
        };
        const bucket = ensureDay(iso);
        bucket.events.push(ev);
        bucket.kinds.add("short-day");
      });

      holidayRequests.value
        .filter((req) => DISPLAYED_CALENDAR_STATUSES.has(req.status))
        .forEach((req) => {
          const startDate = toDateObject(req.start_date);
          const endDate = toDateObject(req.end_date || req.start_date) || startDate;
          if (!startDate) return;

          const color = statusChipColor(req.status, req.pending_action);
          const themedColor =
            color === "green"
              ? "success"
              : color === "amber"
              ? "warning"
              : color === "error"
              ? "error"
              : color === "blue-grey" || color === "grey"
              ? "neutral"
              : color || "primary";
          const kind = req.status === "pending" ? "pending" : "approved";
          const title =
            req.holiday_type_name ||
            lookupHolidayType(req.holiday_type_id) ||
            statusLabel(req.status, req.pending_action);

          const cursor = new Date(startDate);
          while (cursor <= endDate) {
            const iso = formatAsIsoDate(cursor);
            const ev = {
              start: new Date(cursor),
              end: new Date(cursor),
              color: themedColor,
              title,
              allDay: true,
              meta: { kind, iso, highlightKind: null },
            };
            const bucket = ensureDay(iso);
            bucket.events.push(ev);
            bucket.kinds.add(kind);
            cursor.setDate(cursor.getDate() + 1);
          }
        });

      const result = [];
      days.forEach((bucket) => {
        const highlight = decideHighlight(bucket.kinds);
        if (bucket.events.length) {
          bucket.events[0].meta.highlightKind = highlight;
        }
        result.push(...bucket.events);
      });

      return result;
    });

    const eventColorByEvent = (event) => event?.color || "primary";

    function getWorkingDays(request) {
      if (!request) return 0;
      const recorded = toNumber(request.working_days_requested, 0);
      if (recorded) return recorded;
      const start = request.start_date ? normalizeDateString(request.start_date) : "";
      const end = request.end_date ? normalizeDateString(request.end_date) : "";
      return computeWorkingDaysUtil(start, end, { nonWorkingDates: nonWorkingSet.value });
    }

    const statusMapping = computed(() => ({
      pending: { label: t("employeeDetails.statuses.pending"), color: "amber" },
      approved: { label: t("employeeDetails.statuses.approved"), color: "green" },
      rejected: { label: t("employeeDetails.statuses.rejected"), color: "error" },
      cancelled: { label: t("employeeDetails.statuses.cancelled"), color: "grey" },
      draft: { label: t("employeeDetails.statuses.draft"), color: "blue-grey" },
      cancelPending: { label: t("employeeDetails.statuses.cancelPending"), color: "error" },
    }));

    function statusLabel(status, pendingAction = null) {
      if (status === "pending" && pendingAction === "cancel") {
        return statusMapping.value.cancelPending?.label || statusMapping.value.pending?.label || status;
      }
      return statusMapping.value[status]?.label || status;
    }

    function statusChipColor(status, pendingAction = null) {
      if (status === "pending" && pendingAction === "cancel") {
        return statusMapping.value.cancelPending?.color || "error";
      }
      return statusMapping.value[status]?.color || "grey";
    }

    function isPastRequest(request) {
      const end = new Date(request.end_date || request.start_date || request.created_at || Date.now());
      const today = new Date();
      end.setHours(23, 59, 59, 999);
      return end < today;
    }

    const sortedRequests = computed(() => {
      const now = new Date();
      const upcoming = [];
      const past = [];

      holidayRequests.value.forEach((request) => {
        const endDate = new Date(request.end_date || request.start_date || request.created_at || Date.now());
        if (Number.isNaN(endDate.getTime())) {
          upcoming.push(request);
          return;
        }
        const bucket = endDate >= now ? upcoming : past;
        bucket.push(request);
      });

      upcoming.sort((a, b) => {
        const aDate = new Date(a.start_date || a.created_at || 0).getTime();
        const bDate = new Date(b.start_date || b.created_at || 0).getTime();
        return aDate - bDate;
      });

      past.sort((a, b) => {
        const aDate = new Date(a.start_date || a.created_at || 0).getTime();
        const bDate = new Date(b.start_date || b.created_at || 0).getTime();
        return bDate - aDate;
      });

      return [...upcoming, ...past];
    });

    const visibleRequests = computed(() =>
      showAllRequests.value
        ? sortedRequests.value
        : sortedRequests.value.slice(0, Math.max(0, visibleLimit.value))
    );

    const hasMoreRequests = computed(
      () => !showAllRequests.value && sortedRequests.value.length > visibleLimit.value
    );

    function showMore() {
      if (showAllRequests.value) return;
      visibleLimit.value += LOAD_MORE_STEP;
    }

    const toggleShowAllRequests = () => {
      showAllRequests.value = !showAllRequests.value;
      if (!showAllRequests.value) {
        visibleLimit.value = INITIAL_VISIBLE_REQUESTS;
      }
    };

    function onCalendarModelUpdate(value) {
      if (Array.isArray(value)) {
        const normalized = value
          .map((entry) => (entry instanceof Date ? entry : toDateObject(entry)))
          .filter(Boolean);
        calendarSelection.value = normalized;
      } else if (value) {
        const normalized = value instanceof Date ? value : toDateObject(value);
        calendarSelection.value = normalized ? [normalized] : [];
      } else {
        calendarSelection.value = [];
      }
    }

    async function fetchHolidayTypes() {
      try {
        const { data } = await axios.get("/holidays/types");
        holidayTypes.value = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Eroare fetchHolidayTypes:", error);
        holidayTypes.value = [];
      }
    }

    async function fetchNonWorkingDays(companyId) {
      if (!companyId) {
        nonWorkingDays.value = [];
        return;
      }

      const now = new Date();
      let minDate = new Date(now.getFullYear(), 0, 1);
      let maxDate = new Date(now.getFullYear() + 1, 11, 31);

      holidayRequests.value.forEach((req) => {
        const startIso = normalizeDateString(req.start_date);
        const endIso = normalizeDateString(req.end_date);
        const startDate = startIso ? toDateObject(startIso) : null;
        const endDate = endIso ? toDateObject(endIso) : null;
        if (startDate && startDate < minDate) {
          minDate = new Date(startDate);
        }
        if (endDate && endDate > maxDate) {
          maxDate = new Date(endDate);
        }
      });

      try {
        const { data } = await axios.get("/holidays/non-working-days", {
          params: {
            company_id: companyId,
            start_date: formatAsIsoDate(minDate),
            end_date: formatAsIsoDate(maxDate),
          },
        });

        nonWorkingDays.value = Array.isArray(data?.occurrences)
          ? data.occurrences
              .map((occ) => ({
                id: Number(occ.id),
                occurrence_date: normalizeDateString(occ.occurrence_date || occ.date),
                name: occ.name || "",
              }))
              .filter((occ) => occ.occurrence_date)
          : [];
      } catch (error) {
        console.error("Eroare fetchNonWorkingDays:", error);
        nonWorkingDays.value = [];
      }
    }

    async function fetchHolidayData() {
      if (!employee.value?.company_id || !employeeId.value) {
        holidayBalance.value = null;
        holidayRequests.value = [];
        return;
      }

      try {
        const [balanceRes, requestsRes] = await Promise.all([
          axios.get("/holidays/balances", {
            params: {
              company_id: employee.value.company_id,
              employee_id: employeeId.value,
            },
          }),
          axios.get("/holidays/requests", {
            params: {
              company_id: employee.value.company_id,
              employee_id: employeeId.value,
            },
          }),
        ]);
        holidayBalance.value = balanceRes.data?.[0] || null;
        holidayRequests.value = Array.isArray(requestsRes.data) ? requestsRes.data : [];
        companyPdfDisabled.value = Boolean(requestsRes.data?.[0]?.company_pdfrequest);
        await fetchNonWorkingDays(employee.value.company_id);
      } catch (error) {
        console.error("Eroare fetchHolidayData:", error);
        holidayBalance.value = null;
        holidayRequests.value = [];
        nonWorkingDays.value = [];
      }
    }

    async function fetchEmployee() {
      if (!employeeId.value) {
        employee.value = {};
        return;
      }

      try {
        const { data } = await axios.get(`/candidati/employees/${employeeId.value}`);
        employee.value = data || {};
      } catch (error) {
        console.error("Eroare la obținerea datelor angajatului:", error);
        employee.value = {};
      }
    }

    async function loadContext() {
      if (!employeeId.value) {
        employee.value = {};
        holidayBalance.value = null;
        holidayRequests.value = [];
        nonWorkingDays.value = [];
        loading.value = false;
        return;
      }

      loading.value = true;
      try {
        await fetchEmployee();
        await Promise.all([fetchHolidayTypes(), fetchHolidayData()]);
      } finally {
        loading.value = false;
      }
    }

    async function downloadPdf(request, kind = "request") {
      if (companyPdfDisabled.value) {
        alert(t("employeeHolidays.errors.pdfDisabled"));
        return;
      }
      const requestId = typeof request === "object" ? request?.id : request;
      if (!requestId) return;
      pdfDownloadLoadingId.value = `${kind}-${requestId}`;
      try {
        const url =
          kind === "order"
            ? `/holidays/requests/${requestId}/pdf-order`
            : `/holidays/requests/${requestId}/pdf-request`;
        const { data } = await axios.get(url, { responseType: "blob" });
        const blob = new Blob([data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `${kind === "order" ? "ordin" : "cerere"}-concediu-${requestId}.pdf`;
        link.click();
        window.URL.revokeObjectURL(link.href);
        pdfSnack.value = {
          open: true,
          color: "success",
          text: t("employeeHolidays.download.started"),
        };
      } catch (error) {
        console.error("Eroare download PDF:", error);
        const message = error?.response?.data?.message || t("employeeHolidays.download.error");
        pdfSnack.value = {
          open: true,
          color: "error",
          text: message,
        };
      } finally {
        pdfDownloadLoadingId.value = null;
      }
    }

    onMounted(loadContext);

    watch(
      () => employeeId.value,
      () => {
        loadContext();
      }
    );

    watch(sortedRequests, () => {
      if (!showAllRequests.value) {
        visibleLimit.value = INITIAL_VISIBLE_REQUESTS;
      }
    });

    watch(
      pdfSnack,
      (val) => {
        if (val.open && !val.text) {
          pdfSnack.value.open = false;
        }
      },
      { deep: true }
    );

    return {
      allowHolidayRequests,
      employeeId,
      loading,
      t,
      formatNumber,
      formatDate,
      balanceSummary,
      hasBalance,
      sortedRequests,
      visibleRequests,
      showAllRequests,
      toggleShowAllRequests,
      hasMoreRequests,
      showMore,
      statusLabel,
      statusChipColor,
      getWorkingDays,
      companyPdfDisabled,
      pdfDownloadLoadingId,
      pdfSnack,
      downloadPdf,
      isPastRequest,
      lookupHolidayType,
      calendarSelection,
      calendarEvents,
      eventColorByEvent,
      onCalendarModelUpdate,
    };
  },
});
</script>

<style scoped>

.employee-details-ext-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.vacation-overview {
  max-width: 1400px;
  margin: 16px auto 0;
  padding-bottom: 24px;
  padding-top: 10px;
}

.info-card {
  border-radius: 16px;
}

.info-title {
  font-weight: 600;
  font-size: 16px;
  align-items: center;
}

.balance-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
}

.requests-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.request-row {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
}

.request-row__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}
.request-row__meta {
  display: flex;
  align-items: center;
  gap: 4px;
}
.request-row__downloads {
  display: flex;
  align-items: center;
  gap: 2px;
}
.request-row__title {
  display: flex;
  align-items: center;
  gap: 8px;
}
.request-row--past {
  opacity: 0.65;
}

.calendar-card {
  display: flex;
  flex-direction: column;
}

.calendar-wrapper {
  max-width: 520px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  width: 100%;
}

.calendar-card :deep(.v-card-text) {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
}

.vacation-calendar {
  border-radius: 12px;
  width: 100%;
  --v-theme-background: var(--v-theme-surface);
  --v-theme-surface-light: var(--v-theme-surface);
}

.calendar-event-chip {
  display: flex;
  align-items: center;
  gap: 6px;
}

.calendar-event-chip__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
.calendar-card :deep(.v-date-picker) {
  width: 100%;
  max-width: 520px;
}
.calendar-card :deep(.v-calendar-month__days) {
  grid-auto-rows: 100px;
}
.calendar-card :deep(.v-calendar-month__day) {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  min-height: 50px;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}
.calendar-card :deep(.v-calendar-weekly__day-label),
.calendar-card :deep(.v-calendar-weekly__day-content) {
  position: relative;
  z-index: 1;
}
.calendar-card :deep(.v-calendar-month__day:has(.calendar-day-overlay--approved) .v-calendar-weekly__day-label .v-btn__content) {
  color: rgb(var(--v-theme-success));
}
.calendar-card :deep(.v-calendar-month__day:has(.calendar-day-overlay--pending) .v-calendar-weekly__day-label .v-btn__content) {
  color: rgb(var(--v-theme-warning));
}
.calendar-card :deep(.v-calendar-month__day:has(.calendar-day-overlay--non-working) .v-calendar-weekly__day-label .v-btn__content) {
  color: #94a3b8;
}
.calendar-card .v-date-picker {
  --v-date-picker-header-height: 64px;
}

.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: #4a5568;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.legend-dot.approved {
  background-color: #2e7d32;
}

.legend-dot.pending {
  background-color: #fb8c00;
}

.legend-dot.non-working {
  background-color: #94a3b8;
}

.legend-dot.short-day {
  background-color: #2563eb;
}

.calendar-day-overlay {
  position: absolute;
  inset: 4px;
  border-radius: 8px;
  opacity: 0.15;
}

.calendar-day-overlay--non-working {
  background-color: #94a3b8;
}

.calendar-day-overlay--pending {
  background-color: #fb8c00;
}

.calendar-day-overlay--approved {
  background-color: #ef4444;
}

.calendar-day-overlay--short-day {
  background-color: color-mix(in srgb, var(--v-theme-info) 32%, white 68%);
}

.calendar-card :deep(.v-calendar-month__day:has(.calendar-day-overlay--short-day) .v-calendar-weekly__day-label .v-btn__content) {
  color: rgb(var(--v-theme-info));
}
</style>
